/**
 * @fileoverview Giving somebody the key, and the three things this screen
 * refuses to let an author believe.
 *
 * UNTIL THIS EXISTED THE VISIBILITY TOGGLE WAS A PROMISE THE APP COULD NOT KEEP.
 * The page editor offers "Only people I share it with" and says "Nobody else can
 * read it, including us", and every part of that was true except that there was
 * no way to share it with anybody. Key delivery was built, proved against the
 * live relay, and called from no screen. An author could make a private page and
 * the only way to let a person in was to call a module by hand.
 *
 * ---
 *
 * REFUSAL ONE: THERE IS NOTHING TO SHARE BEFORE THE FIRST PUBLISH.
 *
 * A share names an address, and an address only exists once the thing has been
 * published. So this appears after the first publish and not before. Offering it
 * earlier would mean building a control whose only possible outcome is an error.
 *
 * REFUSAL TWO: SHARING CANNOT BE TAKEN BACK, AND IT IS SAID HERE RATHER THAN IN
 * DOCUMENTATION NOBODY READS.
 *
 * Removing somebody later means minting a new key, re-encrypting the body and
 * re-issuing every other recipient. The person removed keeps whatever they
 * already opened, because they hold bytes they already decrypted and no event
 * can reach into their machine. That is the same shape as the visibility flip's
 * "going private is not a retraction", and it is said at the moment of acting
 * for the same reason.
 *
 * REFUSAL THREE: THE LIST BELOW IS NOT AN ACCESS LIST, AND MUST NOT READ LIKE
 * ONE.
 *
 * It is who was SENT a key. Those diverge the moment a key is rotated, and they
 * diverge again for anybody told the key by other means. An access list we
 * cannot enforce is exactly the theatre this design refuses at every other
 * level: a list in front of an endpoint that serves ciphertext protects nothing
 * while convincing people it does. So the wording says "has been sent the key",
 * never "can read this".
 *
 * ---
 *
 * WHO THE RECIPIENT IS, AND IN WHAT ORDER WE TRY TO WORK IT OUT. A pasted key
 * first, because it needs nobody's cooperation and cannot be wrong about who it
 * names. A bare name second, resolved through the one configured name domain --
 * one domain and not a pattern, for the reason the configuration file gives:
 * otherwise a name typed here would send this browser to a third party of the
 * typist's choosing.
 */

import type { ContentKey, SignerInterface } from '@cloistr/auth';
import { useCallback, useEffect, useRef, useState } from 'react';

import { decodeHexAs, decodeIdentifier } from '@/services/nostr/identifiers';
import { resolveName } from '@/services/nostr/nip05';
import { relaysFor } from '@/services/nostr/relays';
import { sharesIssued, shareWith } from '@/services/pages/shareCollection';

/** What the author is being asked about, in their own words. */
export type ShareSubject = 'page' | 'site';

type Sending =
  | { state: 'idle' }
  | { state: 'sending' }
  | { state: 'sent'; who: string }
  | { state: 'failed'; why: string };

/**
 * Turn whatever was typed into a public key.
 *
 * REFUSES AN nsec LOUDLY AND NEVER ECHOES IT. The decoder throws for a secret
 * key rather than returning nothing, because "unsupported" is not the message
 * somebody needs when they have just pasted their private key into a box. The
 * message here says what to do about it and does not repeat what was typed.
 */
async function recipientFrom(typed: string): Promise<{ pubkey: string } | { problem: string }> {
  const raw = typed.trim();
  if (!raw) return { problem: '' };

  try {
    const decoded = decodeIdentifier(raw);
    if (decoded?.type === 'profile') return { pubkey: decoded.pubkey };
    if (decoded) {
      return { problem: 'That is a link to something, not to a person. Paste their key or their name.' };
    }
  } catch {
    return {
      problem:
        'That is a private key. Never paste one anywhere, and treat it as compromised if you have. You want the public one, which starts npub.',
    };
  }

  const hex = decodeHexAs(raw, 'profile');
  if (hex?.type === 'profile') return { pubkey: hex.pubkey };

  if (raw.includes('@')) {
    return {
      problem:
        'Type just the name, without the domain. Only names on this service can be looked up here; for anybody else, paste their key.',
    };
  }

  const resolved = await resolveName(raw);
  if (resolved) return { pubkey: resolved.pubkey };

  return { problem: 'No such name here. Paste their key instead, which always works.' };
}

const short = (pubkey: string) => `${pubkey.slice(0, 8)}…${pubkey.slice(-4)}`;

export function ShareControl({
  subject,
  address,
  contentKey,
  signer,
}: {
  subject: ShareSubject;
  /** The addressable coordinate of the thing being shared. */
  address: string;
  contentKey: ContentKey;
  signer: SignerInterface;
}) {
  const [typed, setTyped] = useState('');
  const [sending, setSending] = useState<Sending>({ state: 'idle' });
  const [already, setAlready] = useState<string[] | null>(null);

  /**
   * READ THROUGH A REF AND KEYED ON THE ADDRESS, never on the signer object.
   * Once a session is restored the auth package mints a new signer on every
   * render, so an effect depending on the object re-runs forever, and this one
   * opens a relay query and asks the signer to decrypt once per share.
   */
  const signerRef = useRef(signer);
  signerRef.current = signer;

  const refresh = useCallback(() => {
    let live = true;
    void sharesIssued(address, signerRef.current, relaysFor([]))
      .then((who) => live && setAlready(who))
      .catch(() => live && setAlready([]));
    return () => {
      live = false;
    };
  }, [address]);

  useEffect(() => refresh(), [refresh]);

  async function send() {
    const who = await recipientFrom(typed);
    if ('problem' in who) {
      if (who.problem) setSending({ state: 'failed', why: who.problem });
      return;
    }
    if (already?.includes(who.pubkey)) {
      setSending({
        state: 'failed',
        why: 'They have already been sent the key for this one.',
      });
      return;
    }

    setSending({ state: 'sending' });
    const result = await shareWith(address, contentKey, who.pubkey, signer, relaysFor([]));

    if (result.status === 'shared') {
      setSending({ state: 'sent', who: short(who.pubkey) });
      setTyped('');
      refresh();
      return;
    }
    if (result.status === 'signer-failed') {
      setSending({ state: 'failed', why: `Your signer refused. ${result.message}` });
      return;
    }
    const reasons = Object.values(result.reasons);
    setSending({
      state: 'failed',
      why: reasons.length
        ? `No relay accepted it. ${reasons.join(' ')}`
        : 'No relay accepted it, and none of them said why.',
    });
  }

  const noun = subject === 'page' ? 'page' : 'site';

  return (
    <div className="editor-field share-section">
      <span className="editor-label">Give somebody the key</span>
      <p className="editor-note">
        They will be able to open this {noun} with their own key, on any client that knows how
        to read it. Nothing is sent to them; the key waits on the relay for them to find.
      </p>

      <div className="collection-editor-add">
        <input
          className="editor-input"
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value);
            setSending({ state: 'idle' });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void send();
            }
          }}
          placeholder="Their npub, or their name here"
          aria-label="Who to give the key to"
          aria-invalid={sending.state === 'failed'}
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={typed.trim() === '' || sending.state === 'sending'}
        >
          {sending.state === 'sending' ? 'Sending…' : 'Send the key'}
        </button>
      </div>

      {sending.state === 'failed' && (
        <p className="editor-note editor-note-warn share-status">{sending.why}</p>
      )}
      {sending.state === 'sent' && (
        <p className="editor-result editor-result-good share-status" role="status">
          Sent to {sending.who}. They can open it from now on.
        </p>
      )}

      <p className="editor-note">
        <strong>This cannot be taken back.</strong> Shutting somebody out later means giving
        this {noun} a new key and sending that to everybody else again, and whatever they have
        already opened stays opened. No message can reach into somebody&rsquo;s machine and
        remove what they already read.
      </p>

      {already !== null && already.length > 0 && (
        <div className="editor-note">
          <p>
            <strong>Has been sent the key:</strong>
          </p>
          <ul className="share-list">
            {already.map((pubkey) => (
              <li className="share-item" key={pubkey}>{short(pubkey)}</li>
            ))}
          </ul>
          <p>
            This is who a key was SENT to, which is not the same as who can read it today. If
            you have given this {noun} a new key since, the people on this list who were not
            sent it again can no longer open it.
          </p>
        </div>
      )}
    </div>
  );
}
